import ExcelJS from 'exceljs';

export const EXCEL_CURRENCY_FMT = '"R$"#,##0.00';
export const EXCEL_BORDER_COLOR = 'FFE5E7EB';

export type ExcelCellValue = string | number | boolean | Date | null | undefined;

export interface ExcelSummaryItem {
  label: string;
  value: ExcelCellValue;
  currency?: boolean;
  bold?: boolean;
}

export interface ExcelTableColumn {
  header: string;
  key: string;
  width?: number;
  currency?: boolean;
  align?: 'left' | 'center' | 'right';
}

export interface ExcelTableOptions {
  zebra?: boolean;
  autoFilter?: boolean;
  freezeHeader?: boolean;
}

const thinBorder: Partial<ExcelJS.Borders> = {
  top: { style: 'thin', color: { argb: EXCEL_BORDER_COLOR } },
  left: { style: 'thin', color: { argb: EXCEL_BORDER_COLOR } },
  bottom: { style: 'thin', color: { argb: EXCEL_BORDER_COLOR } },
  right: { style: 'thin', color: { argb: EXCEL_BORDER_COLOR } },
};

function applyCellStyle(
  cell: ExcelJS.Cell,
  opts?: { bold?: boolean; bg?: string; color?: string; align?: 'left' | 'center' | 'right'; currency?: boolean },
) {
  cell.font = {
    name: 'Calibri',
    size: 11,
    bold: opts?.bold,
    color: opts?.color ? { argb: opts.color } : undefined,
  };
  cell.alignment = {
    vertical: 'middle',
    horizontal: opts?.align ?? 'left',
    wrapText: true,
  };
  if (opts?.bg) {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: opts.bg } };
  }
  if (opts?.currency && typeof cell.value === 'number') {
    cell.numFmt = EXCEL_CURRENCY_FMT;
  }
}

export class ExcelReportBuilder {
  readonly workbook: ExcelJS.Workbook;
  readonly sheet: ExcelJS.Worksheet;
  private rowIndex = 1;
  private maxColumn = 1;
  private headerRowIndex: number | null = null;

  constructor(sheetName: string) {
    this.workbook = new ExcelJS.Workbook();
    this.workbook.creator = 'CarManager';
    this.workbook.created = new Date();
    this.sheet = this.workbook.addWorksheet(sheetName, {
      properties: { defaultRowHeight: 20 },
      pageSetup: {
        paperSize: 9,
        orientation: 'landscape',
        fitToPage: true,
        fitToWidth: 1,
        fitToHeight: 0,
        margins: { left: 0.4, right: 0.4, top: 0.5, bottom: 0.5, header: 0.2, footer: 0.2 },
      },
    });
  }

  private mergeAndStyle(row: number, fromCol: number, toCol: number, value: ExcelCellValue, style?: Parameters<typeof applyCellStyle>[1] & { size?: number }) {
    this.sheet.mergeCells(row, fromCol, row, toCol);
    const cell = this.sheet.getCell(row, fromCol);
    cell.value = value ?? '';
    applyCellStyle(cell, style);
    if (style?.size) {
      cell.font = { ...cell.font, size: style.size };
    }
    this.maxColumn = Math.max(this.maxColumn, toCol);
  }

  addTitle(title: string, columnCount = 6) {
    this.mergeAndStyle(this.rowIndex, 1, columnCount, title, {
      bold: true,
      size: 16,
      bg: 'FF1A1A1A',
      color: 'FFFFFFFF',
    });
    this.sheet.getRow(this.rowIndex).height = 28;
    this.rowIndex += 1;
  }

  addSubtitle(lines: string[], columnCount = 6) {
    for (const line of lines) {
      this.mergeAndStyle(this.rowIndex, 1, columnCount, line, { color: 'FF6B7280' });
      this.rowIndex += 1;
    }
  }

  addBlank(count = 1) {
    this.rowIndex += count;
  }

  addSummary(items: ExcelSummaryItem[], columnCount = 6) {
    const labelCol = 1;
    const valueCol = Math.min(3, columnCount);
    const valueEnd = columnCount;

    for (const item of items) {
      const row = this.rowIndex;
      this.sheet.mergeCells(row, labelCol, row, valueCol);
      const labelCell = this.sheet.getCell(row, labelCol);
      labelCell.value = item.label;
      applyCellStyle(labelCell, { color: 'FF6B7280' });

      this.sheet.mergeCells(row, valueCol + 1, row, valueEnd);
      const valueCell = this.sheet.getCell(row, valueCol + 1);
      valueCell.value = item.currency ? toExcelNumber(item.value) : (item.value ?? '—');
      applyCellStyle(valueCell, {
        bold: item.bold ?? true,
        align: 'right',
        currency: item.currency,
        bg: 'FFF9FAFB',
      });
      this.sheet.getRow(row).eachCell({ includeEmpty: true }, (cell, colNumber) => {
        if (colNumber <= columnCount) cell.border = thinBorder;
      });
      this.rowIndex += 1;
    }
    this.maxColumn = Math.max(this.maxColumn, columnCount);
    this.addBlank();
  }

  addSectionTitle(title: string, columnCount = 6) {
    this.mergeAndStyle(this.rowIndex, 1, columnCount, title, {
      bold: true,
      bg: 'FFF3F4F6',
    });
    this.sheet.getRow(this.rowIndex).height = 22;
    this.rowIndex += 1;
  }

  addTable(columns: ExcelTableColumn[], rows: Record<string, ExcelCellValue>[], options: ExcelTableOptions = {}) {
    const { zebra = true, autoFilter = true, freezeHeader = true } = options;
    const headerRow = this.rowIndex;
    this.headerRowIndex = headerRow;

    columns.forEach((col, index) => {
      const cell = this.sheet.getCell(headerRow, index + 1);
      cell.value = col.header;
      applyCellStyle(cell, {
        bold: true,
        bg: 'FF1A1A1A',
        color: 'FFFFFFFF',
        align: col.align ?? (col.currency ? 'right' : 'left'),
      });
      cell.border = thinBorder;
      this.sheet.getColumn(index + 1).width = col.width ?? 18;
    });
    this.sheet.getRow(headerRow).height = 24;
    this.rowIndex += 1;
    this.maxColumn = Math.max(this.maxColumn, columns.length);

    rows.forEach((rowData, rowOffset) => {
      const row = this.rowIndex + rowOffset;
      columns.forEach((col, colIndex) => {
        const cell = this.sheet.getCell(row, colIndex + 1);
        const raw = rowData[col.key];
        cell.value = col.currency ? toExcelNumber(raw) : (raw ?? '—');
        applyCellStyle(cell, {
          align: col.align ?? (col.currency ? 'right' : 'left'),
          currency: col.currency,
          bg: zebra && rowOffset % 2 === 1 ? 'FFF9FAFB' : undefined,
        });
        cell.border = thinBorder;
      });
    });

    this.rowIndex += rows.length;

    if (autoFilter && rows.length > 0) {
      this.sheet.autoFilter = {
        from: { row: headerRow, column: 1 },
        to: { row: this.rowIndex - 1, column: columns.length },
      };
    }

    if (freezeHeader) {
      this.sheet.views = [{ state: 'frozen', ySplit: headerRow, xSplit: 0 }];
    }
  }

  addKeyValueBlock(title: string, pairs: ExcelSummaryItem[], columnCount = 6) {
    this.addSectionTitle(title, columnCount);
    this.addSummary(pairs, columnCount);
  }

  async download(filename: string) {
    const buffer = await this.workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
    anchor.click();
    URL.revokeObjectURL(url);
  }
}

function toExcelNumber(value: ExcelCellValue): number {
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  if (value == null || value === '') return 0;
  const s = String(value).trim();
  if (s === '') return 0;
  if (/,\d*$/.test(s)) return Number(s.replace(/\./g, '').replace(',', '.')) || 0;
  return parseFloat(s) || 0;
}
